import { GeneratedOpcUaClientCertificate } from "@src/cloud-connect/api/client-configuration/post-generate-opcua-client-certificate";

export enum GatewayOnboardingStatus {
  GATEWAY_NOT_STARTED = "GATEWAY_NOT_STARTED",
  GATEWAY_ONBOARDING_ANNOUNCED = "GATEWAY_ONBOARDING_ANNOUNCED",
  GATEWAY_ONBOARDING_IN_PROGRESS = "GATEWAY_ONBOARDING_IN_PROGRESS",
  GATEWAY_ONBOARDING_COMPLETED = "GATEWAY_ONBOARDING_COMPLETED",
  GATEWAY_ONBOARDING_FAILED = "GATEWAY_ONBOARDING_FAILED",
}

export enum AnnouncementStatus {
  IDLE = "IDLE",
  LOADING = "LOADING",
  ANNOUNCED = "ANNOUNCED",
  WARNING = "WARNING",
  FAILED = "FAILED",
  SUCCESS = "SUCCESS",
}

export interface GatewayDetails {
  namespace?: string;
  gateway_id?: string;
  name?: string;
  vendor_id?: string;
  connection_counter?: number;
  created_at?: string;
  last_seen?: string;
  private_networks?: string[];
  type?: {
    model?: string;
    sub_model?: string;
  };
  gateway_information?: Record<string, unknown>;
  data_sources?: Record<string, unknown>;
}

export interface GatewayOperationLogOutput {
  compressed?: boolean;
  text?: string;
}

export const isGatewayOperationLogOutput = (
  value: unknown,
): value is GatewayOperationLogOutput => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const logOutput = value as GatewayOperationLogOutput;
  return (
    (logOutput.compressed === undefined ||
      typeof logOutput.compressed === "boolean") &&
    (logOutput.text === undefined || typeof logOutput.text === "string")
  );
};

export interface GatewayOperationAdditionalErrorCode {
  type?: string;
  error_code?: number | string;
}

export interface GatewayOperationError {
  error_code?: string;
  additional_error_codes?: GatewayOperationAdditionalErrorCode[];
  title?: string;
  detail?: string;
  severity?: string;
  recommended_action?: string;
  trace_id?: string;
  log_output?: GatewayOperationLogOutput;
}

export const GATEWAY_OPERATION_PROTOCOL_FIELDS: Array<
  [label: string, key: keyof GatewayOperationError]
> = [
  ["Error code", "error_code"],
  ["Title", "title"],
  ["Detail", "detail"],
  ["Severity", "severity"],
  ["Recommended action", "recommended_action"],
  ["Trace id", "trace_id"],
  ["Log output", "log_output"],
];

const formatGatewayOperationProtocolValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return "";
};

export const getGatewayOperationProtocolEntries = (
  details?: GatewayOperationError | null,
): Array<[string, string]> => {
  return GATEWAY_OPERATION_PROTOCOL_FIELDS.map(
    ([label, key]): [string, string] => {
      const value = details?.[key];
      if (key === "log_output" && isGatewayOperationLogOutput(value)) {
        return [label, value.text?.trim() || ""];
      }

      return [label, formatGatewayOperationProtocolValue(value)];
    },
  ).filter(([, value]) => Boolean(value));
};

export enum MachineOnboardingStatus {
  MACHINE_NOT_STARTED = "MACHINE_NOT_STARTED",
  MACHINE_DETAILS_CAPTURED = "MACHINE_DETAILS_CAPTURED",
}

export enum MachineAvailabilityStatus {
  IDLE = "IDLE",
  LOADING = "LOADING",
  IP_REACHABLE = "IP_REACHABLE",
  SUCCESS = "SUCCESS",
  WARNING = "WARNING",
  FAILED = "FAILED",
}

export interface MachineDetails {
  machine_id?: string;
  source_id?: string;
  name?: string;
  hostname?: string;
}

export interface OnboardingDetails {
  gateway: GatewayDetails;
  machine?: MachineDetails;
}

export type MachineOnboardingSummaryStatus =
  | "IDLE"
  | "SUCCESS"
  | "PARTIAL_SUCCESS"
  | "WARNING"
  | "FAILED"
  | "SKIPPED";

export interface MachineOnboardingSummary {
  status: MachineOnboardingSummaryStatus;
  installedSkills: string[];
  failedSkills: Array<{
    type: string;
    error: string;
    details?: GatewayOperationError;
  }>;
  skippedSkills: string[];
}

export interface OnboardingState {
  gatewayOnboardingStatus: GatewayOnboardingStatus;
  announcementStatus: AnnouncementStatus;
  onboardingDetails: OnboardingDetails;
  machineOnboardingStatus: MachineOnboardingStatus;
  machineAvailabilityStatus: MachineAvailabilityStatus;
  machineAvailabilityError: string;
  machineSkillsLabel: string;
  machineOnboardingSummary: MachineOnboardingSummary;
  connectDataSourceClientCertificate: GeneratedOpcUaClientCertificate | null;
}

export interface GetGatewayOperationStatusResponse {
  operation_id?: string;
  operation_type?: string;
  status?:
    | "idle"
    | "in_progress"
    | "success"
    | "error"
    | "partial_success"
    | "timed_out"
    | "failure"
    | "backend_failure"
    | string;
  state?:
    | "IDLE"
    | "IN_PROGRESS"
    | "SUCCESS"
    | "ERROR"
    | "PARTIAL_SUCCESS"
    | "TIMED_OUT"
    | "FAILURE"
    | "BACKEND_FAILURE"
    | string;
  message?: string;
  detail?: string;
  error?: GatewayOperationError;
  namespace?: string;
  result?: {
    hostname?: string;
    reachable?: boolean;
    skills?: Record<string, number | number[] | undefined>;
    [key: string]: unknown;
  };
  hostname?: string;
  source_id?: string;
  reachable?: boolean;
  skills?: Record<string, number | number[] | undefined>;
}
